import { useMemo } from "react";
import { BeaconBase, Project, SiteWithBeacons } from "../../api/models";
import { Map } from "./Map";
import { MapLegendButton } from "./MapLegendButton";
import {
  getPolygonsProjects,
  getPolygonsSites,
  getPolylinesSites,
  transformBeaconsToMarkers,
} from "./mapUtils";
import { useNodeSelectedContext } from "../../contexts/NodeSelectedContext";

interface ConstructionProjectMapProps {
  beacons: BeaconBase[] | undefined;
  constructionProjects: Project[] | undefined;
  constructionSites: SiteWithBeacons[] | undefined;
  isLoading: boolean;
}

export function ConstructionProjectMap({
  beacons,
  constructionProjects,
  constructionSites,
  isLoading,
}: ConstructionProjectMapProps) {
  const { nodeVisitiblyDisabledList, selectedNode, setSelectedNode } =
    useNodeSelectedContext();

  const markers = transformBeaconsToMarkers(beacons, setSelectedNode);

  const filteredMarkers = useMemo(
    () =>
      markers?.filter(
        (marker) => !nodeVisitiblyDisabledList.includes(marker.title)
      ),
    [markers, nodeVisitiblyDisabledList]
  );

  const filteredConstructionSites =
    constructionSites?.filter(
      (site) => site && !nodeVisitiblyDisabledList.includes(site.id)
    ) || [];

  const filteredConstructionProjects =
    constructionProjects?.filter(
      (project) => project && !nodeVisitiblyDisabledList.includes(project.id)
    ) || [];

  const combinedPolygons = [
    ...getPolygonsProjects(
      filteredConstructionProjects,
      "#4C86F7",
      setSelectedNode
    ),
    ...getPolygonsSites(filteredConstructionSites, "yellow", setSelectedNode),
  ];

  const polylines = getPolylinesSites(
    filteredConstructionSites,
    "yellow",
    3,
    setSelectedNode
  );

  return (
    <>
      <MapLegendButton />
      <Map
        markers={filteredMarkers}
        polylines={polylines}
        polygons={combinedPolygons}
        isLoading={isLoading}
        testId="construction-project-map"
        onMapClicked={() => setSelectedNode({ selectedId: "" })}
        updateBounds={selectedNode.selectedType === "title"}
        onMapBoundsChanged={(changed) => setSelectedNode({})}
      />
    </>
  );
}
